import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios, { AxiosError } from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Profile from "../components/profile";
import Support from "../components/support";
import logo from "../assets/HDFC-Ergo-logo.png";
import theme from "../theme";
import { useAuth } from "../provider/auth_provider";

const COMPLIANCE_BASE_URL = "https://fusion-api.itnext-dev.com/kyp_v1";
//   process.env.REACT_APP_COMPLIANCE_BASE_URL;

const MAX_FILE_SIZE_MB = 25;

const getDownloadFilename = (contentDisposition?: string) => {
  if (!contentDisposition) return "compliance-checklist.xlsx";
  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1])
    return decodeURIComponent(utfMatch[1].replace(/['"]/g, ""));
  const match = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
  );
  if (match?.[1]) return match[1].replace(/['"]/g, "");
  return "compliance-checklist.xlsx";
};

const getApiErrorMessage = async (error: unknown) => {
  if (error instanceof AxiosError && error.response?.data instanceof Blob) {
    const text = await error.response.data.text();
    try {
      const json = JSON.parse(text);
      if (typeof json?.detail === "string") return json.detail;
      if (typeof json?.message === "string") return json.message;
    } catch {
      if (text) return text;
    }
  }
  if (error instanceof AxiosError) {
    return (
      error.response?.data?.message ||
      error.message ||
      "Failed to generate checklist. Please try again."
    );
  }
  return "Failed to generate checklist. Please try again.";
};

export default function Compliance() {
  const { userDetails } = useAuth();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const acceptFile = (selected: File | undefined | null) => {
    setErrorMessage("");
    setSuccessMessage("");
    if (!selected) return;
    const isPdf =
      selected.type === "application/pdf" ||
      selected.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setFile(null);
      setErrorMessage("Please upload a PDF file.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFile(null);
      setErrorMessage(`File size must be under ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    setFile(selected);
  };

  const handleGenerate = async () => {
    if (!file) {
      setErrorMessage("Please upload a PDF to generate the checklist.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", userDetails?.name);

      const response = await axios.post(
        `${COMPLIANCE_BASE_URL}/regcheck_ai/generate-checklist`,
        formData,
        {
          responseType: "blob",
          timeout: 10 * 60 * 1000,
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = getDownloadFilename(
        String(response.headers["content-disposition"] || ""),
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(
        "Checklist generated. Your Excel file has been downloaded.",
      );
    } catch (error) {
      setErrorMessage(await getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background:
          "linear-gradient(90deg, #ff817f 0%, #fed0b2 20%, #ffffff 60%)",
        minHeight: "100svh",
      }}
    >
      <Box
        bgcolor={"hsla(215, 15%, 97%, 0.5)"}
        minHeight={"100svh"}
        px={2}
        pt={2}
        pb={3}
        display="flex"
        flexDirection="column"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              sx={{
                color: "#171515",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                "&:hover": { background: "transparent", color: "#E8505B" },
              }}
            >
              Back to dashboard
            </Button>
          </Link>
          <Profile />
        </Box>

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={2}
          mb={1}
        >
          <img
            src={logo}
            alt="HDFC Ergo Logo"
            style={{
              width: isSmallScreen ? "3.75rem" : "5rem",
              height: "auto",
            }}
          />
        </Box>
        <Typography
          sx={{
            textAlign: "center",
            background:
              "linear-gradient(1deg, rgb(232, 80, 91), rgb(255 191 149), rgb(255 253 252))",
            WebkitBackgroundClip: "text !important",
            WebkitTextFillColor: "transparent !important",
            fontWeight: 700,
            fontSize: isSmallScreen ? "2.4rem" : "3.75rem",
            letterSpacing: "-2px",
            lineHeight: 1,
            mb: 0.5,
          }}
        >
          FUSION
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            textDecoration: "underline",
            textUnderlineOffset: "6px",
            textAlign: "center",
            mt: 1,
            mb: 3,
          }}
        >
          Compliance Checklist
        </Typography>

        <Box
          sx={{
            width: "90%",
            maxWidth: "32rem",
            margin: "0 auto",
            padding: isSmallScreen ? "1.25rem" : "1.75rem 2rem",
            borderRadius: "1.25rem",
            background: "white",
            boxShadow: "0rem 0rem 3.75rem 0rem #7777771A",
          }}
        >
          <Typography
            variant="body2"
            color="#0E0E0E"
            fontSize={13}
            textAlign="center"
            mb={2.5}
          >
            Upload a regulatory PDF and generate an Excel checklist.
          </Typography>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(e) => {
              acceptFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />

          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              acceptFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            sx={{
              border: `0.125rem dashed ${isDragging ? "#E8505B" : "#171515"}`,
              borderRadius: "1rem",
              padding: "1.75rem 1rem",
              textAlign: "center",
              cursor: "pointer",
              background: isDragging ? "rgba(232, 80, 91, 0.06)" : "#fafafa",
              transition: "all 0.2s ease",
            }}
          >
            {file ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <InsertDriveFileOutlinedIcon sx={{ color: "#E8505B" }} />
                <Typography fontSize={14} fontWeight={600} color="#0E0E0E">
                  {file.name}
                </Typography>
                <CloseRoundedIcon
                  sx={{ fontSize: 18, color: "#4d4848", ml: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setSuccessMessage("");
                    setErrorMessage("");
                  }}
                />
              </Box>
            ) : (
              <>
                <CloudUploadIcon
                  sx={{ fontSize: 36, color: "#E8505B", mb: 1 }}
                />
                <Typography fontSize={14} fontWeight={600} color="#0E0E0E">
                  Drop your PDF here, or click to upload
                </Typography>
                <Typography fontSize={12} color="#4d4848" mt={0.5}>
                  PDF only · max {MAX_FILE_SIZE_MB} MB
                </Typography>
              </>
            )}
          </Box>

          {errorMessage && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1.5, textAlign: "center" }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                textAlign: "center",
                color: "#2e7d32",
                fontWeight: 600,
              }}
            >
              {successMessage}
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={loading || !file}
            sx={{
              background: "#0E0E0E",
              width: "100%",
              borderRadius: "0.3125rem",
              fontSize: "0.8rem",
              fontWeight: 600,
              marginTop: "1.5rem",
              textTransform: "none",
              "&:hover": { background: "#E8505B" },
              "&.Mui-disabled": {
                background: "#cfcfcf",
                color: "#fff",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} style={{ color: "#fff" }} />
            ) : (
              "Generate Checklist"
            )}
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: "auto",
            pt: 4,
          }}
        >
          <Support />
        </Box>
      </Box>
    </div>
  );
}
